import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { PortalOrder, PortalOrderLine } from '@provalia/shared';
import { Badge, Empty, ErrorBox, Loading, errorText, useAsync, useToast } from '../../shared/ui';
import { fmtDate, fmtMoney, fmtNumber, relDays } from '../../shared/format';
import { api, type OrdersResponse, type SyncReport } from '../api';
import { isBuyer, isStaff, usePortal } from '../context';
import {
  ACCEPTANCE,
  AcceptanceBadge,
  DateStatusBadge,
  DrawingCell,
  GreenLightBadge,
  PriceStatusBadge,
  ReceiptBadge,
  URGENCY,
  UrgencyBadge,
  UrgencyLegend,
  requiredOf,
} from '../components/status';

interface Row {
  order: PortalOrder;
  line: PortalOrderLine;
}

type UrgencyFilter = 'open' | 'late' | 'soon' | 'later' | 'done' | 'all';

const pendingValidation = (l: PortalOrderLine): boolean => l.dateStatus === 'V' || l.priceStatus === 'V';
const pendingReceipt = (r: Row): boolean => r.order.acceptance === 'A' && r.line.open && !r.line.physicalReceipt;

export function Cartera(): JSX.Element {
  const { me } = usePortal();
  const role = me.session.role;
  const staff = isStaff(role);
  const buyer = isBuyer(role);
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [syncing, setSyncing] = useState(false);

  // «Recargar» pide la cartera recién leída de SAP; al abrir la página vale la
  // que el repaso dejó preparada hace un momento.
  const freshNext = useRef(false);
  const { data, error, loading, reload } = useAsync(() => {
    const fresh = freshNext.current;
    freshNext.current = false;
    return api.get<OrdersResponse>(`/portal/orders${fresh ? '?fresh=1' : ''}`);
  }, []);

  // Filtros en la URL: al volver de un pedido se conservan.
  const q = params.get('q') ?? '';
  const acc = params.get('acc') ?? '';
  const urg = (params.get('urg') ?? 'open') as UrgencyFilter;
  const sup = params.get('sup') ?? '';
  const val = params.get('val') === '1';
  // Recepción entra directamente a lo que tiene que recibir.
  const rec = (params.get('rec') ?? (role === 'recepcion' ? '1' : '0')) === '1';

  const setParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const order of data?.orders ?? []) for (const line of order.lines) out.push({ order, line });
    // Pendientes de aceptar primero; luego por fecha de requerimiento (apartado 6.1).
    return out.sort((a, b) => {
      const pa = a.order.acceptance === 'P' ? 0 : 1;
      const pb = b.order.acceptance === 'P' ? 0 : 1;
      if (pa !== pb) return pa - pb;
      const da = requiredOf(a.line) ?? '9999-12-31';
      const db = requiredOf(b.line) ?? '9999-12-31';
      if (da !== db) return da < db ? -1 : 1;
      return a.order.docNum - b.order.docNum || a.line.lineNum - b.line.lineNum;
    });
  }, [data]);

  const suppliers = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of data?.orders ?? []) map.set(o.cardCode, o.cardName);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [data]);

  const stats = useMemo(() => {
    const orders = data?.orders ?? [];
    const open = rows.filter((r) => r.line.open);
    return {
      pendingAccept: orders.filter((o) => o.acceptance === 'P').length,
      late: open.filter((r) => r.line.urgency === 'late').length,
      soon: open.filter((r) => r.line.urgency === 'soon').length,
      validation: open.filter((r) => pendingValidation(r.line)).length,
      receipt: rows.filter(pendingReceipt).length,
    };
  }, [data, rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(({ order, line }) => {
      if (acc && order.acceptance !== acc) return false;
      if (urg === 'open' && !line.open) return false;
      if (urg !== 'open' && urg !== 'all' && line.urgency !== urg) return false;
      if (sup && order.cardCode !== sup) return false;
      if (val && !pendingValidation(line)) return false;
      if (staff && rec && !pendingReceipt({ order, line })) return false;
      if (needle) {
        const hay = `${order.docNum} ${line.itemCode} ${line.description} ${order.cardName} ${order.cardCode}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, acc, urg, sup, val, rec, staff]);

  const filtersActive = q !== '' || acc !== '' || urg !== 'open' || sup !== '' || val || (staff && rec);

  async function syncNow(): Promise<void> {
    setSyncing(true);
    try {
      const { report } = await api.post<{ report: SyncReport }>('/portal/sync');
      if (report.ok) {
        toast(
          `Repaso hecho: ${report.openOrders} pedidos abiertos, ${report.published} publicados, ${report.modified} modificados, ${report.reminders} recordatorios. Correos: ${report.mail.sent} enviados${report.mail.failed ? `, ${report.mail.failed} fallidos` : ''}.`,
          'ok',
        );
      } else {
        toast(`El repaso de SAP ha fallado: ${report.error ?? 'error desconocido'}`, 'bad');
      }
      await reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setSyncing(false);
    }
  }

  const title = role === 'proveedor' ? 'Mis pedidos' : role === 'recepcion' ? 'Recepción en playa' : 'Cartera de pedidos';
  const intro =
    role === 'proveedor'
      ? `Tus pedidos abiertos con ${me.tenant.name}, línea a línea. Pulsa una línea para ver el pedido y actuar.`
      : role === 'recepcion'
        ? 'Líneas de pedidos aceptados que todavía no se han recibido físicamente. Pulsa una para confirmar la llegada.'
        : 'Todas las líneas abiertas de los proveedores del portal. Pulsa una para ver el pedido.';

  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          <p className="muted">{intro}</p>
        </div>
        <div className="row">
          {data ? <span className="subtle">Actualizado a las {hhmm(data.at)}</span> : null}
          <button
            className="btn"
            onClick={() => {
              freshNext.current = true;
              void reload();
            }}
            disabled={loading}
          >
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
          {buyer ? (
            <button
              className="btn"
              onClick={() => void syncNow()}
              disabled={syncing}
              title="Busca en SAP pedidos nuevos o modificados y envía los avisos pendientes"
            >
              {syncing ? 'Repasando SAP…' : 'Repasar SAP ahora'}
            </button>
          ) : null}
        </div>
      </div>

      {role === 'proveedor' && stats.pendingAccept > 0 && acc !== 'P' ? (
        <div className="callout warn pt-banner">
          <div>
            <div className="title">
              {stats.pendingAccept === 1 ? 'Tienes 1 pedido pendiente de aceptar' : `Tienes ${stats.pendingAccept} pedidos pendientes de aceptar`}
            </div>
            <div>Revísalos y acéptalos (o recházalos) para que {me.tenant.name} sepa que los has recibido.</div>
          </div>
          <button className="btn primary" onClick={() => setParam('acc', 'P')}>
            Ver pendientes
          </button>
        </div>
      ) : null}

      <div className="grid cols-4 pt-stats">
        <StatButton label="Pendientes de aceptar" value={stats.pendingAccept} foot="pedidos" tone={stats.pendingAccept ? 'warn' : undefined} active={acc === 'P'} onClick={() => setParam('acc', acc === 'P' ? null : 'P')} />
        <StatButton label="Retrasadas" value={stats.late} foot="líneas abiertas" tone={stats.late ? 'bad' : undefined} active={urg === 'late'} onClick={() => setParam('urg', urg === 'late' ? null : 'late')} />
        <StatButton label="Próximos 15 días" value={stats.soon} foot="líneas abiertas" tone={stats.soon ? 'warn' : undefined} active={urg === 'soon'} onClick={() => setParam('urg', urg === 'soon' ? null : 'soon')} />
        {role === 'recepcion' ? (
          <StatButton label="Pendientes de recibir" value={stats.receipt} foot="líneas aceptadas" active={rec} onClick={() => setParam('rec', rec ? '0' : '1')} />
        ) : (
          <StatButton
            label={staff ? 'Pendientes de validar' : 'En revisión de compras'}
            value={stats.validation}
            foot={staff ? 'fechas o precios fuera de tolerancia' : 'fechas o precios fuera de tolerancia'}
            tone={stats.validation && staff ? 'warn' : undefined}
            active={val}
            onClick={() => setParam('val', val ? null : '1')}
          />
        )}
      </div>

      <section className="card">
        <div className="card-head pt-filters">
          <input
            className="input pt-search"
            type="search"
            placeholder={staff ? 'Buscar pedido, artículo, descripción o proveedor' : 'Buscar pedido, artículo o descripción'}
            aria-label="Buscar"
            value={q}
            onChange={(e) => setParam('q', e.target.value)}
          />
          <select className="select" aria-label="Estado de aceptación" value={acc} onChange={(e) => setParam('acc', e.target.value)}>
            <option value="">Cualquier aceptación</option>
            {(['P', 'A', 'R'] as const).map((s) => (
              <option key={s} value={s}>
                {ACCEPTANCE[s].label}
              </option>
            ))}
          </select>
          <select className="select" aria-label="Plazo" value={urg} onChange={(e) => setParam('urg', e.target.value === 'open' ? null : e.target.value)}>
            <option value="open">Todas las líneas abiertas</option>
            <option value="late">{URGENCY.late.label}</option>
            <option value="soon">{URGENCY.soon.label}</option>
            <option value="later">{URGENCY.later.label}</option>
            <option value="done">Servidas o cerradas</option>
            <option value="all">Todas</option>
          </select>
          {staff ? (
            <select className="select" aria-label="Proveedor" value={sup} onChange={(e) => setParam('sup', e.target.value)}>
              <option value="">Todos los proveedores</option>
              {suppliers.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          ) : null}
          <label className="check">
            <input type="checkbox" checked={val} onChange={(e) => setParam('val', e.target.checked ? '1' : null)} />
            {staff ? 'Solo pendiente de validar' : 'Solo en revisión de compras'}
          </label>
          {staff ? (
            <label className="check">
              <input type="checkbox" checked={rec} onChange={(e) => setParam('rec', e.target.checked ? '1' : '0')} />
              Solo pendiente de recibir
            </label>
          ) : null}
        </div>

        <div className="row between pt-legend-row">
          <UrgencyLegend />
          {data ? (
            <span className="subtle">
              {filtered.length} de {rows.length} líneas
              {filtersActive ? (
                <>
                  {' · '}
                  <button className="pt-link" onClick={() => setParams(new URLSearchParams(role === 'recepcion' ? { rec: '0' } : {}), { replace: true })}>
                    Quitar filtros
                  </button>
                </>
              ) : null}
            </span>
          ) : null}
        </div>

        {error ? (
          <div className="card-body">
            <ErrorBox error={error} onRetry={() => void reload()} />
          </div>
        ) : !data ? (
          <Loading label="Leyendo los pedidos de SAP…" />
        ) : rows.length === 0 ? (
          <Empty icon="📦">
            {role === 'proveedor' ? (
              <>
                <strong>No tienes pedidos abiertos ahora mismo.</strong>
                <div>Cuando {me.tenant.name} te envíe un pedido recibirás un correo y aparecerá aquí.</div>
              </>
            ) : data.suppliersEnabled === 0 ? (
              <>
                <strong>Todavía no hay ningún proveedor dado de alta en el portal.</strong>
                <div style={{ maxWidth: 560, margin: '6px auto 14px' }}>
                  La cartera enseña los pedidos abiertos de los proveedores que des de alta. Búscalos en {me.tenant.name === '' ? 'SAP' : `el SAP de ${me.tenant.name}`} y
                  dales acceso: sus pedidos aparecerán aquí y podrás invitar a sus usuarios.
                </div>
                {buyer ? (
                  <Link className="btn primary" to="/proveedores">
                    Dar de alta proveedores
                  </Link>
                ) : null}
              </>
            ) : (
              <>
                <strong>No hay pedidos abiertos de proveedores del portal.</strong>
                <div>
                  Da de alta proveedores en <Link to="/proveedores">Proveedores</Link>
                  {buyer ? ' y pulsa «Repasar SAP ahora» para publicar sus pedidos.' : '.'}
                </div>
              </>
            )}
          </Empty>
        ) : filtered.length === 0 ? (
          <Empty icon="🔍">
            <strong>Ninguna línea coincide con los filtros.</strong>
            <div style={{ marginTop: 10 }}>
              <button className="btn" onClick={() => setParams(new URLSearchParams(role === 'recepcion' ? { rec: '0' } : {}), { replace: true })}>
                Quitar filtros
              </button>
            </div>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table pt-cartera">
              <thead>
                <tr>
                  <th>Pedido</th>
                  {staff ? <th>Proveedor</th> : null}
                  <th>Artículo</th>
                  <th>Plano</th>
                  <th className="num">Pendiente</th>
                  <th>Requerida</th>
                  <th>Comprometida / propuesta</th>
                  {staff ? <th className="num">Precio</th> : null}
                  {staff ? <th>Albarán</th> : null}
                  {staff ? <th>Recepción</th> : null}
                  {staff ? <th>Luz verde</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.map(({ order, line }) => {
                  const href = `/pedidos/${order.docEntry}`;
                  const required = requiredOf(line);
                  return (
                    <tr
                      key={`${order.docEntry}-${line.lineNum}`}
                      className={`clickable ${URGENCY[line.urgency].row}`}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('a,button')) return;
                        navigate(href);
                      }}
                    >
                      <td className="nowrap">
                        <Link to={href} className="pt-docnum">
                          {order.docNum}
                        </Link>
                        <span className="subtle"> · L{line.lineNum + 1}</span>
                        <div className="pt-cell-badges">
                          <AcceptanceBadge status={order.acceptance} />
                          {order.changes.length > 0 ? <Badge tone="info">Modificado</Badge> : null}
                        </div>
                      </td>
                      {staff ? (
                        <td>
                          <div className="pt-clip">{order.cardName}</div>
                          <div className="subtle">{order.cardCode}</div>
                        </td>
                      ) : null}
                      <td>
                        <div className="mono">{line.itemCode}</div>
                        <div className="subtle pt-clip" title={line.description}>
                          {line.description}
                        </div>
                      </td>
                      <td>
                        <DrawingCell docEntry={order.docEntry} lineNum={line.lineNum} version={line.drawingVersion} />
                      </td>
                      <td className="num nowrap">
                        <strong>{fmtNumber(line.openQuantity)}</strong>
                        <span className="subtle"> de {fmtNumber(line.quantity)}</span>
                      </td>
                      <td className="nowrap">
                        <div>{fmtDate(required)}</div>
                        {line.open ? <UrgencyBadge urgency={line.urgency} /> : <span className="subtle">Servida</span>}
                      </td>
                      <td className="nowrap">
                        <CommittedCell line={line} />
                      </td>
                      {staff ? <td className="num nowrap">{fmtMoney(line.unitPrice, line.currency)}</td> : null}
                      {staff ? (
                        <td className="nowrap">
                          {line.deliveryNoteNumber ? <div className="subtle">Nº {line.deliveryNoteNumber}</div> : null}
                          <PriceStatusBadge status={line.priceStatus} />
                        </td>
                      ) : null}
                      {staff ? (
                        <td className="nowrap">
                          <ReceiptBadge line={line} />
                        </td>
                      ) : null}
                      {staff ? (
                        <td className="nowrap">
                          <GreenLightBadge on={line.greenLight} />
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function CommittedCell({ line }: { line: PortalOrderLine }): JSX.Element {
  const showProposed = line.proposedDate !== null && (line.dateStatus === 'V' || line.dateStatus === 'R');
  return (
    <div className="stack sm" style={{ gap: 3 }}>
      {line.confirmedDate ? (
        <div>
          <strong>{fmtDate(line.confirmedDate)}</strong>
          {line.open ? <span className="subtle"> · {relDays(line.confirmedDate)}</span> : null}
        </div>
      ) : null}
      {showProposed ? <div className="subtle">Propuesta {fmtDate(line.proposedDate)}</div> : null}
      <div>
        <DateStatusBadge status={line.dateStatus} />
      </div>
    </div>
  );
}

function StatButton({
  label,
  value,
  foot,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  foot: string;
  tone?: 'ok' | 'warn' | 'bad';
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" className={`card stat pt-stat ${tone ?? ''} ${active ? 'active' : ''}`} onClick={onClick} aria-pressed={active}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className="foot">{active ? 'Filtrando · pulsa para quitar' : foot}</div>
    </button>
  );
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' });
}
